/**
 * A browser-side port of the backend's `XmlTemplateEngine`, for the local template sandbox only.
 *
 * Nothing rendered here is ever submitted anywhere — the real document a country receives is always
 * rendered server-side by `GET /api/export-requests/{id}/xml-preview`. This exists so a superadmin
 * can try a template against made-up values without raising an export request first.
 *
 * Kept deliberately close to `XmlTemplateEngine.java`, construct for construct and rejection for
 * rejection, because two engines that disagree are worse than one engine and no sandbox.
 */

export type Lookup = (term: string) => unknown;

/** Markup to splice verbatim rather than escape — the one unescaped path, as on the server. */
export class Raw {
  constructor(readonly markup: string) {}
}

/** A syntax error in the template itself, matching the server's `INVALID_TEMPLATE`. */
export class TemplateError extends Error {}

type Node =
  | { kind: 'literal'; text: string }
  | { kind: 'scalar'; term: string }
  | { kind: 'each'; term: string; body: Node[] };

/** Inside an `each` body, the element itself rather than one of its fields. */
export const SELF = '.';

const EACH_OPEN = '#each';
const EACH_CLOSE = '/each';

function invalid(what: string): TemplateError {
  return new TemplateError(`This template has ${what}`);
}

/** One pass, which is also the syntax check — the server validates by parsing for the same reason. */
function parse(template: string): Node[] {
  const top: Node[] = [];
  let body: Node[] | null = null;
  let eachTerm = '';
  let literalFrom = 0;

  const tag = /\{\{\s*([^{}]*?)\s*\}\}/g;
  let match: RegExpExecArray | null;

  while ((match = tag.exec(template)) !== null) {
    const sink = body ?? top;
    if (match.index > literalFrom) {
      sink.push({ kind: 'literal', text: template.slice(literalFrom, match.index) });
    }
    literalFrom = match.index + match[0].length;
    const name = match[1].trim();

    if (name === EACH_CLOSE) {
      if (body === null) {
        throw invalid('a {{/each}} with no matching {{#each}}');
      }
      top.push({ kind: 'each', term: eachTerm, body });
      body = null;
      eachTerm = '';
    } else if (name.startsWith('/')) {
      throw invalid(`an unknown closing tag {{${name}}}; only {{/each}} exists`);
    } else if (name.startsWith('#')) {
      if (name !== EACH_OPEN && !name.startsWith(`${EACH_OPEN} `)) {
        throw invalid(`an unknown block tag {{${name}}}; only {{#each term}} exists`);
      }
      if (body !== null) {
        throw invalid('a nested {{#each}}; one level of repetition is all this template language supports');
      }
      const term = name.slice(EACH_OPEN.length).trim();
      if (!term) {
        throw invalid('an {{#each}} naming no term');
      }
      eachTerm = term;
      body = [];
    } else if (!name) {
      throw invalid('an empty {{}} placeholder');
    } else {
      sink.push({ kind: 'scalar', term: name });
    }
  }

  if (body !== null) {
    throw invalid(`an unclosed {{#each ${eachTerm}}}`);
  }
  if (literalFrom < template.length) {
    top.push({ kind: 'literal', text: template.slice(literalFrom) });
  }
  return top;
}

/** XML-escapes a value for either element text or an attribute. */
export function escapeXml(value: string): string {
  let out = '';
  for (const c of value) {
    switch (c) {
      case '&': out += '&amp;'; break;
      case '<': out += '&lt;'; break;
      case '>': out += '&gt;'; break;
      case '"': out += '&quot;'; break;
      case "'": out += '&apos;'; break;
      default: out += c;
    }
  }
  return out;
}

/**
 * A displayable string, or null when there is nothing sensible to render.
 *
 * Unwraps the EPCIS `{code, value}` pair the middleware really sends. An object *without* a `value`
 * key is a structure, not a scalar, and yields null rather than leaking object syntax into a
 * document — which nudges the author towards `{{#each}}`, the construct that can render it.
 */
function scalarText(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value)) {
    const parts = value.map(scalarText).filter((text): text is string => text !== null);
    return parts.length === 0 ? null : parts.join(' ');
  }
  if (typeof value === 'object') {
    const inner = (value as Record<string, unknown>)['value'];
    return inner === null || inner === undefined ? null : String(inner);
  }
  return String(value);
}

/** A null term yields no iterations; a scalar yields one, so a single-element list still reads. */
function elementsOf(value: unknown): unknown[] {
  if (value === null || value === undefined) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

function fieldOf(element: unknown, term: string): unknown {
  if (term === SELF) {
    return element;
  }
  if (element !== null && typeof element === 'object' && !Array.isArray(element)) {
    return (element as Record<string, unknown>)[term];
  }
  return null;
}

function appendAll(nodes: Node[], lookup: Lookup, out: string[]): void {
  for (const node of nodes) {
    if (node.kind === 'literal') {
      out.push(node.text);
    } else if (node.kind === 'scalar') {
      const value = lookup(node.term);
      if (value instanceof Raw) {
        out.push(value.markup);
      } else {
        const text = scalarText(value);
        if (text !== null) {
          out.push(escapeXml(text));
        }
      }
    } else {
      for (const element of elementsOf(lookup(node.term))) {
        // An each body contains no further each, so this recurses exactly once.
        appendAll(node.body, (term) => fieldOf(element, term), out);
      }
    }
  }
}

/** Renders a template, escaping every substituted value. */
export function render(template: string, lookup: Lookup): string {
  const out: string[] = [];
  appendAll(parse(template), lookup, out);
  return out.join('');
}

/**
 * The terms this template binds at the top level, split by the construct that binds them.
 *
 * Field tags *inside* an `each` body are excluded, exactly as the server's `referencedTerms` is:
 * they address a field of a list element, which is not a general term and has no catalog to be
 * checked against.
 */
export function analyse(template: string): { scalars: string[]; lists: string[] } {
  const scalars = new Set<string>();
  const lists = new Set<string>();
  for (const node of parse(template)) {
    if (node.kind === 'scalar') {
      scalars.add(node.term);
    } else if (node.kind === 'each') {
      lists.add(node.term);
    }
  }
  return { scalars: [...scalars], lists: [...lists] };
}

/** The template with every placeholder resolved to nothing, and every `each` body dropped. */
export function skeleton(template: string): string {
  return render(template, () => null);
}

/**
 * Whether this parses as XML.
 *
 * A doctype is refused outright rather than parsed, mirroring the server's `disallow-doctype-decl`
 * — there is no reason a submission template should carry one, and refusing keeps the two engines
 * answering the same question.
 */
export function isWellFormed(xml: string): boolean {
  if (/<!DOCTYPE/i.test(xml)) {
    return false;
  }
  try {
    const parsed = new DOMParser().parseFromString(xml, 'application/xml');
    return parsed.getElementsByTagName('parsererror').length === 0;
  } catch {
    return false;
  }
}

/**
 * Whether this parses as a whole document or as sibling elements — an EVENT template is a fragment
 * spliced into an envelope, so it has no obligation to carry a single root of its own.
 */
export function isWellFormedDocumentOrFragment(xml: string): boolean {
  return isWellFormed(xml) || isWellFormed(`<fragment>${xml}</fragment>`);
}
